import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconLoader2 } from "@tabler/icons-react";
import { cn } from "../../lib/utils";

export type SegmentedItem<T extends string> = {
  value: T;
  label: string;
  // The leading icon node — caller owns its sizing (e.g. `size-3.5`), so the
  // control stays agnostic to icon set / dimensions.
  icon: React.ReactNode;
  // A disabled segment can't be selected and reads in a muted mint.
  disabled: boolean;
  // A loading segment spins after its label while its content is on its way.
  loading: boolean;
};

// Geometry of the active segment, relative to the pill's padding box — what the
// sliding thumb is sized/positioned to. Null until first measured.
type Thumb = { x: number; y: number; w: number; h: number };

/**
 * Floating segmented control — a blurred white pill grouping mutually exclusive
 * segments, the active one filled dark moss (Figma DS 5.0 "Tab Toggle", node
 * 6066:2400). The fill is a single thumb that slides (and resizes, since labels
 * differ in width) to the active segment. Used as the JuisciGPT Answer/Sources
 * switch; generic over the value union so call sites stay exhaustively typed.
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  items,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  items: SegmentedItem<T>[];
  className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [thumb, setThumb] = useState<Thumb | null>(null);

  const activeIndex = items.findIndex((item) => item.value === value);
  // Re-measure when the selection moves or any label or spinner changes width.
  const layoutKey = items
    .map((item) => `${item.value}:${item.label}:${String(item.loading)}`)
    .join("|");

  useLayoutEffect(() => {
    function measure(): void {
      const container = containerRef.current;
      const btn = btnRefs.current[activeIndex];
      if (container === null || btn === null || btn === undefined) return;
      const containerRect = container.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      // Subtract the pill's border (clientLeft/Top) so the coords are relative
      // to the padding box — the origin an absolutely-positioned child uses.
      setThumb({
        x: btnRect.left - containerRect.left - container.clientLeft,
        y: btnRect.top - containerRect.top - container.clientTop,
        w: btnRect.width,
        h: btnRect.height,
      });
    }
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
    };
  }, [activeIndex, layoutKey]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative inline-flex items-center gap-1.5 rounded-ds-button-lg bg-white/84 p-1 shadow-[0px_2px_8px_rgba(0,0,0,0.08)] backdrop-blur-md",
        className
      )}
    >
      {thumb !== null && (
        <span
          aria-hidden
          className="absolute left-0 top-0 rounded-ds-button-md bg-ds-moss-900 transition-[transform,width,height] duration-100 ease-out motion-reduce:transition-none"
          style={{
            transform: `translate(${thumb.x}px, ${thumb.y}px)`,
            width: thumb.w,
            height: thumb.h,
          }}
        />
      )}
      {items.map((item, index) => {
        const isActive = item.value === value;
        return (
          <button
            key={item.value}
            ref={(el) => {
              btnRefs.current[index] = el;
            }}
            type="button"
            disabled={item.disabled}
            onClick={() => {
              if (item.disabled) return;
              onChange(item.value);
            }}
            className={cn(
              "relative z-10 flex items-center gap-1.5 rounded-ds-button-md px-3 py-2 font-dm-sans text-footnote font-semibold transition-colors duration-100",
              item.disabled
                ? "text-ds-moss-700/40 cursor-default"
                : "cursor-pointer",
              isActive
                ? "text-ds-white"
                : item.disabled
                  ? null
                  : "text-ds-moss-700"
            )}
          >
            {item.icon}
            {item.label}
            {item.loading && (
              <IconLoader2
                className="size-3.5 animate-spin"
                aria-label={t("Loading…")}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
